import { ref, uploadBytes, getDownloadURL, deleteObject } from 'firebase/storage';
import { storage } from './config';

/**
 * Upload an image to Firebase Storage
 * @param {File} file - Image file to upload
 * @param {string} folder - Folder name (e.g., 'products', 'media-bank')
 * @returns {Promise<string>} - Download URL of uploaded image
 */
export const uploadImage = async (file, folder = 'media-bank') => {
  try {
    // Create unique filename with timestamp
    const timestamp = Date.now();
    const filename = `${timestamp}_${file.name.replace(/[^a-zA-Z0-9.-]/g, '_')}`;
    const filePath = `${folder}/${filename}`;

    // Create storage reference
    const storageRef = ref(storage, filePath);

    // Upload file
    console.log('Uploading image:', filename);
    const snapshot = await uploadBytes(storageRef, file);

    // Get download URL
    const downloadURL = await getDownloadURL(snapshot.ref);
    console.log('Image uploaded successfully:', downloadURL);

    return downloadURL;
  } catch (error) {
    console.error('Error uploading image:', error);
    throw error;
  }
};

/**
 * Delete an image from Firebase Storage
 * @param {string} imageUrl - Full URL of the image to delete
 */
export const deleteImage = async (imageUrl) => {
  try {
    // Extract path from URL
    const path = getPathFromUrl(imageUrl);
    if (!path) {
      throw new Error('Invalid image URL');
    }

    const imageRef = ref(storage, path);
    await deleteObject(imageRef);
    console.log('Image deleted successfully');
  } catch (error) {
    console.error('Error deleting image:', error);
    throw error;
  }
};

/**
 * Extract storage path from Firebase Storage URL
 */
const getPathFromUrl = (url) => {
  try {
    const baseUrl = 'https://firebasestorage.googleapis.com/v0/b/';
    if (!url.startsWith(baseUrl)) return null;

    const path = url.split('/o/')[1]?.split('?')[0];
    return path ? decodeURIComponent(path) : null;
  } catch (error) {
    return null;
  }
};

/**
 * Validate image file
 */
export const validateImageFile = (file) => {
  const maxSize = 5 * 1024 * 1024; // 5MB
  const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp', 'image/gif'];

  if (!allowedTypes.includes(file.type)) {
    throw new Error('Invalid file type. Only JPG, PNG, WebP, and GIF are allowed.');
  }

  if (file.size > maxSize) {
    throw new Error('File too large. Maximum size is 5MB.');
  }

  return true;
};
